import { DecimalPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { CreationButtons } from '@shared/components/ui/creation-buttons/creation-buttons';
import { ErrorService } from '@shared/services/error.service';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { LabelComponent } from '@shared/components/form/label/label.component';
import { Modal } from '@shared/components/common/modal/modal';
import { SelectFieldComponent, SelectOption } from '@shared/components/form/select/select-field.component';
import { SnackbarService } from '@shared/services/snackbar.service';
import { TextareaFieldComponent } from '@shared/components/form/textarea/textarea-field.component';

import {
  ClienteComboItem,
  MotivoSalidaStock,
  SalidaStockPayload,
  StockVitrificadoEmbrion,
} from '../embriones.interface';
import { EmbrionesService } from '../embriones.service';

interface DialogData {
  stockId: number;
  embriones: StockVitrificadoEmbrion[];
  /** Fecha de vitrificación (ISO). Un embrión no puede salir antes de haber entrado. */
  fechaVit: string | null;
}

@Component({
  selector: 'app-registrar-salida-stock',
  imports: [
    AppFormComponent,
    CreationButtons,
    DecimalPipe,
    InputFieldComponent,
    LabelComponent,
    Modal,
    ReactiveFormsModule,
    SelectFieldComponent,
    TextareaFieldComponent,
  ],
  templateUrl: './registrar-salida-stock.html',
})
export class RegistrarSalidaStock implements OnInit {
  form!: FormGroup;
  loading = false;
  loadingClientes = false;

  clientes: ClienteComboItem[] = [];

  readonly motivosOptions: SelectOption[] = [
    { value: 'D', label: 'Donación' },
    { value: 'V', label: 'Venta' },
    { value: 'P', label: 'Pérdida' },
  ];

  readonly monedasOptions: SelectOption[] = [
    { value: 'PYG', label: 'Guaraníes (PYG)' },
    { value: 'USD', label: 'Dólares (USD)' },
  ];

  get clientesOptions(): SelectOption[] {
    return this.clientes.map(cliente => ({ value: cliente.id, label: cliente.nombre }));
  }

  get embriones(): StockVitrificadoEmbrion[] {
    return this.dialogData.embriones;
  }

  /** Una pajuela puede agrupar hasta 6 embriones: lo que sale es el total, no la cantidad de pajuelas. */
  get totalEmbriones(): number {
    return this.embriones.reduce((total, embrion) => total + embrion.nro_embriones, 0);
  }

  get motivo(): MotivoSalidaStock | null {
    return (this.form?.get('motivo')?.value as MotivoSalidaStock) || null;
  }

  get requiereClienteDestino(): boolean {
    return this.motivo === 'D' || this.motivo === 'V';
  }

  get requiereMonto(): boolean {
    return this.motivo === 'V';
  }

  get observacionRequerida(): boolean {
    return this.motivo === 'P';
  }

  /** El monto se carga por embrión; el total de la operación depende de cuántos salen. */
  get montoTotal(): number {
    return this.numberValue('monto_unitario') * this.totalEmbriones;
  }

  /** Mensaje inline cuando la fecha elegida cae antes de la vitrificación. */
  get errorFechaAnterior(): string | null {
    const error = this.form?.get('fecha')?.errors?.['anteriorAVitrificacion'];

    return error ? `La fecha de salida no puede ser anterior a la fecha de vitrificación (${error.fechaVit}).` : null;
  }

  private fB = inject(FormBuilder);
  private embrionesService = inject(EmbrionesService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<RegistrarSalidaStock>);
  private dialogData: DialogData = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.makeForm();
    this.loadClientes();
  }

  private makeForm(): void {
    this.form = this.fB.group({
      motivo: ['', Validators.required],
      fecha: [this.fechaSalidaPorDefecto(), [Validators.required, this.fechaNoAnteriorAVitrificacion()]],
      cli_cliente_destino_id: [''],
      monto_unitario: [''],
      moneda: ['PYG'],
      observacion: [''],
    });

    // Cada motivo pide datos distintos; los validadores siguen al motivo elegido.
    this.form.get('motivo')?.valueChanges.subscribe(() => this.syncValidatorsPorMotivo());
  }

  private syncValidatorsPorMotivo(): void {
    const clienteDestino = this.form.get('cli_cliente_destino_id');
    const monto = this.form.get('monto_unitario');
    const moneda = this.form.get('moneda');
    const observacion = this.form.get('observacion');

    clienteDestino?.setValidators(this.requiereClienteDestino ? [Validators.required] : []);
    monto?.setValidators(this.requiereMonto ? [Validators.required, Validators.min(0)] : []);
    moneda?.setValidators(this.requiereMonto ? [Validators.required] : []);
    observacion?.setValidators(this.observacionRequerida ? [Validators.required] : []);

    if (!this.requiereClienteDestino) {
      clienteDestino?.setValue('', { emitEvent: false });
    }

    if (!this.requiereMonto) {
      monto?.setValue('', { emitEvent: false });
    }

    clienteDestino?.updateValueAndValidity({ emitEvent: false });
    monto?.updateValueAndValidity({ emitEvent: false });
    moneda?.updateValueAndValidity({ emitEvent: false });
    observacion?.updateValueAndValidity({ emitEvent: false });
  }

  private loadClientes(): void {
    this.loadingClientes = true;

    this.embrionesService.getClientesDestino().subscribe({
      next: (response) => {
        this.clientes = response.data || [];
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loadingClientes = false;
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();

    const payload: SalidaStockPayload = {
      pajuela_ids: this.embriones.map(embrion => embrion.id),
      motivo: raw.motivo as MotivoSalidaStock,
      fecha: raw.fecha,
      cli_cliente_destino_id: this.requiereClienteDestino ? Number(raw.cli_cliente_destino_id) : null,
      monto_unitario: this.requiereMonto ? Number(raw.monto_unitario) : null,
      moneda: this.requiereMonto ? raw.moneda : null,
      observacion: raw.observacion || null,
    };

    this.loading = true;

    this.embrionesService.registrarSalidaStock(this.dialogData.stockId, payload).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.snackbarService.showSnackBar('Salida de stock registrada correctamente');
          this.dialogRef.close(response.data);
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loading = false;
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  /**
   * El contrato interno de las fechas es ISO (yyyy-mm-dd), así que alcanza con
   * comparar los strings; no hace falta construir Date.
   */
  private fechaNoAnteriorAVitrificacion(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const fechaVit = this.fechaVit();

      if (!fechaVit || !control.value || control.value >= fechaVit) {
        return null;
      }

      return { anteriorAVitrificacion: { fechaVit: this.aFechaLegible(fechaVit) } };
    };
  }

  private numberValue(controlName: string): number {
    return Number(this.form?.get(controlName)?.value ?? 0) || 0;
  }

  private aFechaLegible(iso: string): string {
    const [anho, mes, dia] = iso.split('-');

    return `${dia}/${mes}/${anho}`;
  }

  /**
   * Un embrión no puede salir antes de haber entrado al stock: el piso de la
   * fecha de salida es la vitrificación, no el día de hoy. Si el stock se cargó
   * con una fecha futura, se propone esa; si no, hoy.
   */
  private fechaSalidaPorDefecto(): string {
    const hoy = this.hoy();
    const fechaVit = this.fechaVit();

    return fechaVit && fechaVit > hoy ? fechaVit : hoy;
  }

  /** La API emite `fecha_vit` cruda: puede venir como datetime, así que se recorta a yyyy-mm-dd. */
  private fechaVit(): string | null {
    const fechaVit = this.dialogData.fechaVit?.slice(0, 10) || null;

    return fechaVit && /^\d{4}-\d{2}-\d{2}$/.test(fechaVit) ? fechaVit : null;
  }

  private hoy(): string {
    const hoy = new Date();
    const mes = `${hoy.getMonth() + 1}`.padStart(2, '0');
    const dia = `${hoy.getDate()}`.padStart(2, '0');

    return `${hoy.getFullYear()}-${mes}-${dia}`;
  }
}
